import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Image, ScrollView } from 'react-native';
import FeatherIcon from 'react-native-vector-icons/Feather';
import { useAuth } from '../../context/AuthContext';
import { Colors } from '../../theme/colors';

import { navigationRef } from '../../navigation/AppNavigator.web';
import { useInboxUnread } from '../../hooks/useInboxUnread';
import { UnreadBadge } from './UnreadBadge';
import { MotionDiv } from '../motion/Motion';
import { MOTION } from '../../utils/motionPresets';
import { SIDEBAR_WIDTH } from '../../utils/webLayout';

interface NavItemProps {
  name: string;
  label: string;
  iconName: string;
  /** Child routes that should keep this entry highlighted (e.g. a detail page). */
  activeFor?: string[];
}

const NAV_ITEMS: NavItemProps[] = [
  { name: 'Home', label: 'Dashboard', iconName: 'home' },
  { name: 'Profile', label: 'Student profile', iconName: 'user' },
  { name: 'Academics', label: 'Academics', iconName: 'book' },
  { name: 'Attendance', label: 'Attendance', iconName: 'calendar' },
  { name: 'Leaves', label: 'Leaves', iconName: 'file-text' },
  { name: 'Fees', label: 'Fees & payments', iconName: 'credit-card' },
  // CXC / CSEC Registration is reached from the Home dashboard's own banner
  // while a sitting is open, not from a permanent sidebar entry.
  { name: 'Store', label: 'Store', iconName: 'shopping-bag' },
  { name: 'Wallet', label: 'Wallet', iconName: 'dollar-sign' },
  { name: 'Canteen', label: 'Canteen', iconName: 'coffee' },
  // Documents hidden for now. Kept (not deleted) so it can be switched back on;
  // the screen and its route are untouched.
  // { name: 'Documents', label: 'Documents', iconName: 'award', activeFor: ['DocumentRequestDetail'] },
  // Hidden until the backend has a real transport module — the screen only had
  // placeholder data. Kept (not deleted) so it can be switched back on.
  // { name: 'Transport', label: 'Transport', iconName: 'truck' },
  { name: 'Communication', label: 'Communication', iconName: 'message-square' },
  { name: 'Settings', label: 'Settings', iconName: 'settings' },
];

export function WebSidebar({ currentRoute, isOverlay, onClose }: { currentRoute: string, isOverlay?: boolean, onClose?: () => void }) {
  const { selectedStudent, logout } = useAuth();
  // New chat messages + events, on the Communication item (hooks/useInboxUnread).
  const { total: inboxUnread } = useInboxUnread();

  const sidebarContent = (
    <View style={[styles.sidebar, isOverlay && styles.sidebarOverlay]}>
      {/* Brand Header */}
      <View style={styles.brandContainer}>
        <View style={styles.brandLogo}>
          <Image source={require('../../assets/images/cayman_icon.png')} style={{ width: 32, height: 32 }} resizeMode="contain" />
        </View>
        <Text style={styles.brandTitle}>Cayman Academy</Text>
      </View>

      <Text style={styles.menuTitle}>MY KID</Text>

      {/* Navigation Links */}
      <ScrollView
        style={styles.navContainer}
        showsVerticalScrollIndicator={true}
        nativeID="sidebar-nav-scroll"
      >
        {NAV_ITEMS.map((item) => {
          const isActive = currentRoute === item.name || !!item.activeFor?.includes(currentRoute);
          return (
            <TouchableOpacity
              key={item.name}
              style={styles.navItem}
              onPress={() => {
                if (navigationRef.isReady()) {
                  // Explicitly clears any backTo the target route might already
                  // carry from a previous quick-link visit (e.g. Home's "Pay
                  // Fees" card sets backTo: 'Home' on the Fees route) - without
                  // this, navigating here via the sidebar could still show a
                  // leftover "back to Home" from an earlier, unrelated visit.
                  navigationRef.navigate(item.name as any, { backTo: undefined } as any);
                }
                // On a phone the menu is a drawer over the page: choosing a page closes it, so the
                // page just chosen is what the parent sees (it used to stay open on top).
                if (isOverlay) onClose?.();
              }}
            >
              {/* One highlight that glides to the new page's entry instead of jumping. */}
              {isActive && (
                <MotionDiv
                  layoutId={isOverlay ? 'web-sidebar-active-drawer' : 'web-sidebar-active'}
                  transition={MOTION.spring}
                  style={activePillStyle}
                  aria-hidden
                />
              )}
              <FeatherIcon
                name={item.iconName}
                size={20}
                color={isActive ? '#FFFFFF' : 'rgb(214 221 230)'}
                style={styles.navIcon}
              />
              <Text style={[styles.navLabel, isActive && styles.navLabelActive]} numberOfLines={1}>
                {item.label}
              </Text>
              {item.name === 'Communication' && <UnreadBadge count={inboxUnread} testID="sidebar-communication-badge" />}
            </TouchableOpacity>
          );
        })}
      </ScrollView>

      {/* User Footer */}
      <View style={styles.footerContainer}>
        {selectedStudent && (
          <View style={styles.studentInfo}>
            <View style={styles.studentAvatar}>
              <Text style={styles.studentAvatarText}>
                {(() => {
                  const name = selectedStudent.name || '';
                  const parts = name.trim().split(' ');
                  if (parts.length > 1) {
                    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
                  }
                  return name.substring(0, 2).toUpperCase();
                })()}
              </Text>
            </View>
            <View style={styles.studentDetails}>
              <Text style={styles.studentName} numberOfLines={1}>
                {selectedStudent.name}
              </Text>
              <Text style={styles.studentClass} numberOfLines={1}>
                Grade {selectedStudent.class}
              </Text>
            </View>
            {isOverlay && onClose && (
              <TouchableOpacity
                style={styles.closeSidebarButton}
                onPress={onClose}
                accessibilityLabel="Close sidebar"
              >
                <FeatherIcon name="chevrons-left" size={18} color="#FFFFFF" />
              </TouchableOpacity>
            )}
          </View>

        )}

        {isOverlay && (
          <TouchableOpacity style={styles.logoutButton} onPress={logout}>
            <FeatherIcon name="log-out" size={16} color="#FF7A59" />
            <Text style={styles.logoutText}>Sign out</Text>
          </TouchableOpacity>
        )}
      </View>
    </View>
  );

  // The drawer slides in over a fading backdrop, and back out (AnimatePresence in
  // AppNavigator.web.tsx keeps it mounted until it has).
  if (isOverlay) {
    return (
      <View style={styles.overlayContainer}>
        <MotionDiv
          style={drawerBackdropStyle}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.22 }}
          onClick={onClose}
          aria-hidden
        />
        <MotionDiv
          style={drawerPanelStyle}
          initial={{ x: -MOTION.drawerWidth }}
          animate={{ x: 0 }}
          exit={{ x: -MOTION.drawerWidth }}
          transition={MOTION.softSpring}
        >
          {sidebarContent}
        </MotionDiv>
      </View>
    );
  }

  return sidebarContent;
}

// Plain CSS for MotionDiv (a DOM div on web), matching the RN styles they replace.
const activePillStyle = {
  position: 'absolute',
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
  borderRadius: 8,
  backgroundColor: 'rgba(255, 255, 255, 0.1)',
  // Under the icon and label, above nothing else: the item is its own stacking context.
  zIndex: -1,
};

const drawerBackdropStyle = {
  position: 'absolute',
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
  backgroundColor: 'rgba(0,0,0,0.5)',
};

const drawerPanelStyle = {
  // Positioned, so it paints above the backdrop.
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
};

const styles = StyleSheet.create({
  sidebar: {
    width: SIDEBAR_WIDTH,
    backgroundColor: '#032158', // Dark blue background
    paddingTop: 24,
    paddingBottom: 24,
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  sidebarOverlay: {
    height: '100%',
  },
  overlayContainer: {
    position: 'absolute' as any,
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 1000,
    flexDirection: 'row',
  },
  brandContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 24,
    marginBottom: 40,
  },
  brandLogo: {
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
  },
  brandLogoText: {
    color: '#FFFFFF',
    fontWeight: 'bold',
    fontSize: 16,
  },
  brandTitle: {
    color: '#FFFFFF',
    fontSize: 18,
    fontWeight: 'bold',
  },
  menuTitle: {
    color: 'rgb(118 129 148)',
    fontSize: 11,
    fontWeight: '700',
    paddingHorizontal: 24,
    marginBottom: 16,
    letterSpacing: 1,
  },
  navContainer: {
    flex: 1,
    paddingHorizontal: 12,
  },
  navItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 12,
    borderRadius: 8,
    marginBottom: 4,
  },
  navIcon: {
    marginRight: 16,
  },
  navLabel: {
    flex: 1,
    color: 'rgb(214 221 230)',
    fontSize: 15,
    fontWeight: '500',
  },
  navLabelActive: {
    color: '#FFFFFF',
    fontWeight: '600',
  },
  footerContainer: {
    paddingHorizontal: 24,
    borderTopWidth: 1,
    borderTopColor: 'rgba(255, 255, 255, 0.1)',
    paddingTop: 24,
  },
  studentInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
  },
  studentAvatar: {
    width: 44,
    height: 44,
    backgroundColor: '#1C75E8',
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
  },
  studentAvatarText: {
    color: '#FFFFFF',
    fontWeight: '800',
    fontSize: 16,
  },
  studentDetails: {
    flex: 1,
  },
  studentName: {
    color: '#FFFFFF',
    fontWeight: '800',
    fontSize: 14,
  },
  studentClass: {
    color: '#8BA1C2',
    fontSize: 12,
    fontWeight: '500',
    marginTop: 2,
  },
  closeSidebarButton: {
    width: 32,
    height: 32,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    marginLeft: 8,
  },
  logoutButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.15)',
    borderRadius: 10,
    marginTop: 4,
  },
  logoutText: {
    color: '#FF7A59',
    marginLeft: 8,
    fontSize: 14,
    fontWeight: '700',
  },
} as any);
